import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Accordion } from 'react-bootstrap';

const QuotationModal = ({ show, onHide, quotationData }) => {
  const [quotations, setQuotations] = useState([]);
  const [expandedQuotation, setExpandedQuotation] = useState(null);

  useEffect(() => {
    if (quotationData && Array.isArray(quotationData)) {
      setQuotations(quotationData);
    } else if (quotationData && !Array.isArray(quotationData)) {
      setQuotations([quotationData]);
    } else {
      setQuotations([]);
    }
  }, [quotationData]);

  // Check if data exists
  const hasData = quotations && quotations.length > 0;

  // Function to parse package info
  const parsePackageInfo = (packageInfo) => {
    try {
      return JSON.parse(packageInfo);
    } catch (error) {
      return [];
    }
  };

  // Function to parse additional services
  const parseAdditionalServices = (additionals) => {
    try {
      return JSON.parse(additionals);
    } catch (error) {
      return [];
    }
  };

  // Function to render single quotation card
  const renderQuotationCard = (quotation, index) => {
    const packageInfo = parsePackageInfo(quotation.pl_package_info);
    const additionalServices = quotation.additionals;

    return (
      <div className="card mb-4" key={quotation.id || index}>
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Quotation #{quotation.request_no}</h5>
            <span className="badge bg-light text-dark">
              {new Date(quotation.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="card-body">
          {/* Header Information */}
          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="border-bottom pb-2">Request Information</h6>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold">Request No:</td>
                    <td>{quotation.request_no || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Request Via:</td>
                    <td>{quotation.request_via || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Shipment Mode:</td>
                    <td className="text-uppercase">{quotation.shipment_mode || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Service Type:</td>
                    <td>{quotation.service_type || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="col-md-6">
              <h6 className="border-bottom pb-2">Customer Information</h6>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold">Full Name:</td>
                    <td>{quotation.full_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Email:</td>
                    <td>{quotation.email || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Phone:</td>
                    <td>{quotation.phone || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Origin & Destination */}
          <div className="row mb-3">
            <div className="col-md-6">
              <h6 className="border-bottom pb-2">Origin Details</h6>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold">Type:</td>
                    <td>{quotation.origin_type || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Port:</td>
                    <td>{quotation.origin_port_full_name || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="col-md-6">
              <h6 className="border-bottom pb-2">Destination Details</h6>
              <table className="table table-sm table-borderless">
                <tbody>
                  <tr>
                    <td className="fw-bold">Type:</td>
                    <td>{quotation.destination_type || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Port:</td>
                    <td>{quotation.destination_port_full_name || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Package Details */}
          {packageInfo && packageInfo.length > 0 && (
            <div className="mb-3">
              <h6 className="border-bottom pb-2">Package Details</h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Package Type</th>
                      <th>Dimensions (L×W×H)</th>
                      <th>Unit</th>
                      <th>Gross Weight</th>
                      <th>Total Boxes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageInfo.map((pkg, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{pkg.package_type_id || 'N/A'}</td>
                        <td>{pkg.length} × {pkg.width} × {pkg.height}</td>
                        <td>{pkg.dimensionunit || 'N/A'}</td>
                        <td>{pkg.gross_weight} {pkg.gross_weight_unit}</td>
                        <td>{pkg.total_boxes || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Additional Services */}
          {additionalServices && additionalServices.length > 0 && (
            <div className="mb-3">
              <h6 className="border-bottom pb-2">Additional Services</h6>
              <div className="d-flex flex-wrap gap-2">
                {additionalServices.map((service, idx) => (
                  <span key={idx} className="badge bg-info">
                    Service {service?.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Status Information */}
          <div className="row">
            <div className="col-md-12">
              <hr />
              <small className="text-muted">
                Cargo Ready Date: {quotation.cargo_ready_date || 'N/A'} | 
                Status: {quotation.status ? 'Active' : 'Inactive'} |
                Last Updated: {quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleString() : 'N/A'}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Quotation Details 
          {hasData && <span className="ms-2 badge bg-info">({quotations.length} Found)</span>}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {!hasData ? (
          // No Data Found UI
          <div className="text-center py-5">
            <div className="mb-4">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="64" 
                height="64" 
                fill="currentColor" 
                className="bi bi-inbox text-muted" 
                viewBox="0 0 16 16"
              >
                <path d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4H4.98zm9.954 5H10.45a2.5 2.5 0 0 1-4.9 0H1.066l.77 4.242A1 1 0 0 0 2.828 14h10.344a1 1 0 0 0 .992-.758L14.934 9zM.5 8.5L3.314 4.314A1.5 1.5 0 0 1 4.556 3h6.888a1.5 1.5 0 0 1 1.242.686L15.5 8.5l-.992 4.961A2 2 0 0 1 12.534 15H3.466a2 2 0 0 1-1.974-1.539L.5 8.5z"/>
              </svg>
            </div>
            <h5 className="text-muted mb-3">No Quotation Data Found</h5>
            <p className="text-muted mb-0">
              No quotations found for the provided email or phone number.
            </p>
          </div>
        ) : (
          <div className="quotations-list">
            {quotations.map((quotation, index) => renderQuotationCard(quotation, index))}
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {hasData && (
          <Button variant="primary" onClick={() => window.print()}>
            Print Quotations
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default QuotationModal;